import { useMemo } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';

import { IFoodlaCategoryOption } from 'models/category';
import { ROOT_CATEGORY_ID } from 'components/constants-ts';
import { CategoryTreeType } from 'components/product/ProductUpdateForm/fields/category/types';

interface ICategoryData {
  id: string;
  name: string;
  parentCategoryId?: string;
  parentTreeId?: string;
  includeFields?: string[];
  excludeFields?: string[];
}

export const GET_ALL_CATEGORIES = gql`
  query getAllCategories($categoryType: CategoryType) {
    allCategories(categoryType: $categoryType) {
      id
      name
      parentCategoryId
      parentTreeId
      coopCategoryId
      coopCategory
      icaCategory
      apoteaCategory
    }
  }
`;

const getChildren = (category: ICategoryData, categories: ICategoryData[]): IFoodlaCategoryOption[] => {
  const filterChildFn = ({ parentCategoryId, parentTreeId }: ICategoryData) => {
    if (category.parentTreeId && parentTreeId) {
      return `${category.parentTreeId}_${category.id}` === parentTreeId;
    }
    return parentCategoryId === category.id;
  }

  const children = categories.filter(filterChildFn);
  if (!children.length) return [];
  return (
    categories
      .filter(filterChildFn)
      // remove [parentCategoryId] field
      .map(({ parentCategoryId, ...category }) => ({ ...category, children: getChildren(category, categories) }))
  );
};

function useCategoriesData({ type = 'foodla', skip = false }: { type?: CategoryTreeType; skip?: boolean } = {}) {
  const { data, loading, error } = useQuery<{ allCategories: ICategoryData[] }>(
    GET_ALL_CATEGORIES,
    { variables: { categoryType: type.toUpperCase() }, skip },
  );

  const categories = useMemo(() => data?.allCategories ?? [], [data]);

  const rootCategory = useMemo<IFoodlaCategoryOption | undefined>(() => {
    if (!categories.length) return undefined;

    const rootData = categories.find(({ id }) => id === ROOT_CATEGORY_ID);
    if (!rootData) return undefined;
    // remove [parentCategoryId] field
    const { parentCategoryId, ...root } = rootData;

    return { ...root, children: getChildren(root, categories) };
  }, [categories]);

  return { categories, rootCategory, loading, error: error?.message };
}

export default useCategoriesData;
